import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  completeNewPassword,
  confirmPasswordReset,
  describeResetError,
  requestPasswordReset,
  signIn,
  type NewPasswordRequired,
} from '../lib/auth';
import { getConfig } from '../lib/config';
import { ErrorNotice } from '../components/Status';
import './Login.css';

/** Dot-field parameters, from the design. */
const DOT_SPACING = 34;
const INFLUENCE_RADIUS = 190;
const DOT_PULL = 14;
const HUE = 265;

interface Dot {
  x: number;
  y: number;
  /** Phase offset, so the idle bob is not in lockstep across the grid. */
  phase: number;
}

/**
 * Draws the dot field that leans toward the pointer, and moves the glow with it.
 *
 * Everything here is imperative on purpose: it runs every frame, and putting
 * pointer position into React state would re-render the form sixty times a
 * second for a purely decorative effect.
 */
function useDotField(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  glowRef: React.RefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Decoration only, so honour a reduced-motion preference by never starting.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let dots: Dot[] = [];
    // Seeded on the first build, not here: the resting position is the centre of
    // the canvas, and the canvas has no size yet. Starting these off-screen (as
    // a "no pointer yet" sentinel) means that until the first pointermove the
    // glow eases away out of view and every dot sits at baseline alpha - which
    // is exactly what someone sees if they land here from a redirect and tab
    // straight into the email field without moving the mouse.
    let seeded = false;
    const pointer = { x: 0, y: 0 };
    const eased = { x: 0, y: 0 };
    const glow = { x: 0, y: 0 };

    const build = () => {
      const rect = canvas.getBoundingClientRect();
      // Capped at 2: past that the dot count costs more than it shows.
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      dots = [];
      const cols = Math.ceil(width / DOT_SPACING) + 1;
      const rows = Math.ceil(height / DOT_SPACING) + 1;
      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          dots.push({ x: i * DOT_SPACING, y: j * DOT_SPACING, phase: Math.random() * Math.PI * 2 });
        }
      }

      // First build only: settle everything on the centre, so the field is lit
      // behind the card from the first frame. Later rebuilds are resizes, where
      // the pointer position is still current and must not be thrown away.
      if (!seeded) {
        seeded = true;
        pointer.x = width / 2;
        pointer.y = height / 2;
        eased.x = width / 2;
        eased.y = height / 2;
        glow.x = width / 2;
        glow.y = height / 2;
      }
    };

    const onMove = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = event.clientX - rect.left;
      pointer.y = event.clientY - rect.top;
    };
    const onLeave = () => {
      pointer.x = width / 2;
      pointer.y = height / 2;
    };

    let frameId = 0;
    let t = 0;
    const frame = () => {
      t += 0.012;
      eased.x += (pointer.x - eased.x) * 0.09;
      eased.y += (pointer.y - eased.y) * 0.09;
      glow.x += (pointer.x - glow.x) * 0.06;
      glow.y += (pointer.y - glow.y) * 0.06;

      if (glowRef.current) {
        glowRef.current.style.transform = `translate(${glow.x}px, ${glow.y}px)`;
      }

      ctx.clearRect(0, 0, width, height);
      for (const dot of dots) {
        const dx = dot.x - eased.x;
        const dy = dot.y - eased.y;
        const near = Math.max(0, 1 - Math.hypot(dx, dy) / INFLUENCE_RADIUS);
        // Squared, so the pull falls away sharply instead of over the whole field.
        const falloff = near * near;
        const angle = Math.atan2(dy, dx);
        const bob = Math.sin(t * 1.6 + dot.phase) * 0.9;

        const x = dot.x + Math.cos(angle) * DOT_PULL * falloff + bob;
        const y = dot.y + Math.sin(angle) * DOT_PULL * falloff + bob;
        const size = 0.9 + falloff * 1.9;
        const alpha = 0.1 + falloff * 0.72;
        const lightness = (0.55 + falloff * 0.32).toFixed(3);
        const chroma = (0.01 + falloff * 0.13).toFixed(3);

        ctx.fillStyle = `oklch(${lightness} ${chroma} ${HUE} / ${alpha.toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
      }

      frameId = requestAnimationFrame(frame);
    };

    build();
    window.addEventListener('resize', build);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerleave', onLeave);
    frameId = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', build);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerleave', onLeave);
    };
  }, [canvasRef, glowRef]);
}

/**
 * Which form the card shows. `forgot` asks for the email to send a code to;
 * `reset` takes that code and a new password. The temporary-password challenge
 * is separate state, because it carries a Cognito session.
 */
type Mode = 'sign_in' | 'forgot' | 'reset';

const HEADINGS: Record<Mode | 'challenge', { title: string; subtitle: string; submit: string; busy: string }> = {
  sign_in: {
    title: 'Sign in',
    subtitle: 'Use your work account to set up roles and review candidate screens.',
    submit: 'Continue',
    busy: 'Signing in…',
  },
  forgot: {
    title: 'Reset your password',
    subtitle: 'Enter your work email and we will send you a code to choose a new password.',
    submit: 'Send code',
    busy: 'Sending…',
  },
  reset: {
    title: 'Choose a new password',
    subtitle: 'Enter the code from the email, then your new password.',
    submit: 'Set new password',
    busy: 'Saving…',
  },
  challenge: {
    title: 'Choose a password',
    subtitle: 'This account still has its temporary password. Pick a new one to continue.',
    submit: 'Set password and continue',
    busy: 'Saving…',
  },
};

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/';

  const [mode, setMode] = useState<Mode>('sign_in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<NewPasswordRequired | undefined>();
  const [notice, setNotice] = useState<string>();
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  useDotField(canvasRef, glowRef);

  const heading = HEADINGS[challenge ? 'challenge' : mode];

  function switchMode(next: Mode) {
    setMode(next);
    setError(undefined);
    setNotice(undefined);
    setCode('');
    setNewPassword('');
  }

  async function sendCode() {
    await requestPasswordReset(email.trim());
    setMode('reset');
    // Deliberately the same whether or not the address has an account.
    setNotice(`If ${email.trim()} has an account, a code is on its way. It can take a minute to arrive.`);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      if (challenge) {
        await completeNewPassword(challenge, newPassword);
      } else if (mode === 'forgot') {
        await sendCode();
        return;
      } else if (mode === 'reset') {
        await confirmPasswordReset(email.trim(), code.trim(), newPassword);
        switchMode('sign_in');
        setPassword('');
        setNotice('Your password has been changed. Sign in with the new one.');
        return;
      } else {
        const result = await signIn(email.trim(), password);
        if (result.kind === 'new_password_required') {
          // Admin-created accounts arrive with a temporary password.
          setChallenge(result);
          return;
        }
      }
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(mode === 'sign_in' || challenge ? err : describeResetError(err));
    } finally {
      setBusy(false);
    }
  }

  async function resendCode() {
    setError(undefined);
    setBusy(true);
    try {
      await sendCode();
    } catch (err) {
      setError(describeResetError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <canvas ref={canvasRef} className="login__canvas" aria-hidden="true" />
      <div ref={glowRef} className="login__glow" aria-hidden="true" />

      <div className="login__card">
        <div className="login__brand">
          <div className="login__mark" aria-hidden="true" />
          <span className="login__brand-name">{getConfig().companyName}</span>
        </div>

        <h1 className="login__title">{heading.title}</h1>
        <p className="login__subtitle">{heading.subtitle}</p>

        <form className="login__form" onSubmit={submit}>
          <ErrorNotice error={error} />
          {notice ? (
            <p className="login__notice" role="status">
              {notice}
            </p>
          ) : null}

          {challenge ? null : mode === 'reset' ? (
            <label className="login__field" htmlFor="code">
              <span className="login__label">Code</span>
              <input
                id="code"
                className="login__input"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
          ) : (
            <label className="login__field" htmlFor="email">
              <span className="login__label">Email</span>
              <input
                id="email"
                className="login__input"
                type="email"
                autoComplete="username"
                placeholder="you@company.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
          )}

          {!challenge && mode === 'sign_in' ? (
            <div className="login__field">
              <label className="login__field" htmlFor="password">
                <span className="login__label">Password</span>
                <input
                  id="password"
                  className="login__input"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
              <button type="button" className="login__link" onClick={() => switchMode('forgot')}>
                Forgot password?
              </button>
            </div>
          ) : null}

          {challenge || mode === 'reset' ? (
            <label className="login__field" htmlFor="new-password">
              <span className="login__label">New password</span>
              <input
                id="new-password"
                className="login__input"
                type="password"
                autoComplete="new-password"
                placeholder="At least 12 characters"
                required
                minLength={12}
                autoFocus={Boolean(challenge)}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </label>
          ) : null}

          <button className="login__submit" type="submit" disabled={busy}>
            {busy ? heading.busy : heading.submit}
          </button>

          {!challenge && mode !== 'sign_in' ? (
            <div className="login__links">
              <button type="button" className="login__link" onClick={() => switchMode('sign_in')}>
                Back to sign in
              </button>
              {mode === 'reset' ? (
                <button type="button" className="login__link" onClick={() => void resendCode()} disabled={busy}>
                  Send a new code
                </button>
              ) : null}
            </div>
          ) : null}
        </form>

        <p className="login__note" style={{ marginTop: 28 }}>
          Accounts are created by an administrator. Ask your hiring team if you need one.
        </p>
      </div>
    </div>
  );
}
